/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { EmailAttachment } from '@gh/builder-shared';
import {
  JobChangeOrderRestService,
  JobVariationStatus,
  PriceBookRestService,
  PriceBookUsage, PrinterRestService,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  DictionaryService,
  getRouteParam,
  getRouteQueryParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { AutocompleteItem, EMPTY_PRODUCE_STATUS, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$, switchMap$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';
import {
  JobChangeOrderElementIdsGetResponse,
  JobCoDetails,
  jobCoDetailsFromDto,
  JobCoDetailsGeneratedDocumentSet,
  JobCoDetailsPrintRequest,
  JobCoDetailsProduceStatus,
  JobCoLetter,
  JobCoProduceRequest,
  JobCoProduceResponse,
  JobCoReplaceOrdersRequest,
  JobCoSave,
  jobCoSaveFromDto,
  jobCoSaveToDto,
  jobCoSaveToPreviewDto,
} from './job-co-details.model';
import { of$ } from '@gh/rx';
import { NotificationService } from '@gh/core-ui';

@Injectable()
export class JobCoDetailsBusinessService implements InitViewService {
  constructor(private jobChangeOrderRest: JobChangeOrderRestService,
              private priceBookRest: PriceBookRestService,
              private printerRest: PrinterRestService,
              private router: AppRouter,
              private notification: NotificationService,
              private dictionaries: DictionaryService,
              private gridLayouts: GridLayoutService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    const changeOrderId = getRouteParam(route, 'changeOrderId');
    const sourceChangeOrderId = getRouteQueryParam(route, 'cloneFrom');

    return {
      jobCoElementGridLayout: this.gridLayouts.get('jobCoElements'),
      statusDict: this.dictionaries.getByEnum(JobVariationStatus),
      details: isRouteForCreationMode(route)
        ? (sourceChangeOrderId ? this.clone(jobId, sourceChangeOrderId) : this.init(jobId))
        : this.get(jobId, changeOrderId),
      produceStatus: changeOrderId ? this.getProduceStatus(jobId, changeOrderId) : EMPTY_PRODUCE_STATUS,
      printers: this.printerRest.findAll(),
    };
  }

  init(jobId: ID): Observable<JobCoDetails> {
    try {
      return this.jobChangeOrderRest.init(jobId).pipe(map$(jobCoDetailsFromDto));
    } catch (error) {
      this.notification.processAll(error);
      this.router.navigateBack();
      return of$({} as JobCoDetails);
    }
  }

  clone(jobId: ID, changeOrderId: ID): Observable<JobCoDetails> {
    return this.jobChangeOrderRest.clone(jobId, changeOrderId).pipe(map$(jobCoDetailsFromDto));
  }

  get(jobId: ID, changeOrderId: ID): Observable<JobCoDetails> {
    return this.jobChangeOrderRest.get(jobId, changeOrderId).pipe(
      map$(jobCoDetailsFromDto),
      switchMap$((details) => this.getProduceStatus(jobId, changeOrderId).pipe(
        map$((produceStatus) => ({ ...details, produceStatus })))));
  }

  save(changeOrder: JobCoSave): Observable<JobCoSave> {
    const dto = jobCoSaveToDto(changeOrder, true);
    const update$ = changeOrder.variationId
      ? this.jobChangeOrderRest.update(changeOrder.jobId, changeOrder.variationId, dto)
      : this.jobChangeOrderRest.create(changeOrder.jobId, dto);
    return update$.pipe(
      map$(jobCoSaveFromDto),
      switchMap$((details) => this.getProduceStatus(changeOrder.jobId, details.variationId).pipe(
        map$((produceStatus) => ({ ...details, produceStatus })))));
  }

  recalculate(changeOrder: JobCoSave): Observable<JobCoSave> {
    return this.jobChangeOrderRest.recalculate(
      changeOrder.jobId,
      changeOrder.variationId,
      jobCoSaveToDto(changeOrder, false)).pipe(
      map$(jobCoSaveFromDto),
      // restore state of isPrintOnProcess flag, because recalculate should not reset it
      map$((model) => ({ ...model, isPrintOnProcess: changeOrder.isPrintOnProcess })));
  }

  generateChangeOrderIds(jobId: ID,
                         itemsAmount: number,
                         componentsAmount: number): Observable<JobChangeOrderElementIdsGetResponse> {
    return this.jobChangeOrderRest.generateChangeOrderIds(jobId, itemsAmount, componentsAmount);
  }

  findAllPriceBooksByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.priceBookRest.findAllAsAutocompleteDataSource({
      autocomplete: true,
      searchString: query.filter,
      showExpired: false,
      showUnreleased: false,
      usage: [PriceBookUsage.ChangeOrders],
    }).select(query).pipe(map$(getSlicedData));
  }

  confirmProduce(jobId: ID, changeOrderId: ID, letter: JobCoLetter): Observable<JobCoLetter> {
    return this.jobChangeOrderRest.confirmProduce(jobId, changeOrderId, letter);
  }

  getProduceStatus(jobId: ID, changeOrderId: ID): Observable<JobCoDetailsProduceStatus> {
    return this.jobChangeOrderRest.getProduceStatus(jobId, changeOrderId);
  }

  generateDocuments(jobId: ID, changeOrderId: ID): Observable<JobCoDetailsGeneratedDocumentSet> {
    return this.jobChangeOrderRest.generateDocuments(jobId, changeOrderId);
  }

  previewDocument(jobId: ID, changeOrderId: ID, changeOrder: JobCoSave): Observable<JobCoLetter> {
    return this.jobChangeOrderRest.previewDocument(jobId, changeOrderId, jobCoSaveToPreviewDto(changeOrder));
  }

  produceChangeOrderDoc(jobId: ID, changeOrderId: ID, changeOrder: JobCoSave): Observable<EmailAttachment> {
    return this.jobChangeOrderRest.produceChangeOrderDoc(
      jobId,
      changeOrderId,
      jobCoSaveToPreviewDto(changeOrder));
  }

  printDocuments(jobId: ID, changeOrderId: ID, request: JobCoDetailsPrintRequest): Observable<any> {
    return this.jobChangeOrderRest.printDocuments(jobId, changeOrderId, request);
  }

  gets3Link(jobId: ID): Observable<any> {
    return this.jobChangeOrderRest.getS3Link(jobId);
  }

  produce(jobId: ID, changeOrderId: ID, request: JobCoProduceRequest): Observable<JobCoProduceResponse> {
    return this.jobChangeOrderRest.produce(jobId, changeOrderId, request);
  }

  replaceOrders(jobId: ID, changeOrderId: ID, request: JobCoReplaceOrdersRequest): Observable<JobCoProduceResponse> {
    return this.jobChangeOrderRest.replaceOrders(jobId, changeOrderId, request);
  }
}
